import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { LANG } from "constants/constants";
import { WEEK_STATES, formatWeekRange, parseWeekDate } from "utils/fafHelper";

// Status label per chip state. Same keys as the former carousel cells: the
// wording of the five states does not change, only where they are displayed.
const STATE_LABELS = {
  [WEEK_STATES.SUBMITTED]: {
    id: "tools_manager_faf_week_status_submitted",
    label: "Déclarée",
  },
  [WEEK_STATES.DRAFT]: {
    id: "tools_manager_faf_week_status_draft",
    label: "En cours",
  },
  [WEEK_STATES.OPEN]: {
    id: "tools_manager_faf_week_status_active",
    label: "À déclarer",
  },
  [WEEK_STATES.BLOCKED]: {
    id: "tools_manager_faf_week_status_blocked",
    label: "Bloquée",
  },
  [WEEK_STATES.LOCKED]: {
    id: "tools_manager_faf_week_status_locked",
    label: "À venir",
  },
};

// The five weeks of GET /declaration/window as a strip of chips, above the card
// and always on screen. It replaces the full-width carousel that used to be a
// step of its own: the declared week never leaves the eye, and a week in draft
// is resumed by clicking its chip — the same `onResume` as before.
const WeekStrip = ({
  weeks,
  currentWeekStart,
  openWeekStart,
  onResume,
  lang,
}) => {
  const intl = useIntl();
  const locale = lang === LANG.FR ? "fr-FR" : "en-US";

  // A week is blocked because an older one is still waiting: the cell said so
  // over three lines, the chip says it on hover rather than growing taller.
  // The hint rides on the wrapping span, NOT on the chip: a blocked chip is
  // always disabled, and browsers fire no pointer event on a disabled control,
  // so a `title` sitting there would never show. It is also repeated inside the
  // chip as screen-reader-only text, which the disabled state does not hide.
  const blockedHint = (state) =>
    state === WEEK_STATES.BLOCKED && openWeekStart
      ? intl.formatMessage(
          {
            id: "tools_manager_faf_week_blocked_hint",
            defaultMessage: "Rattrape d'abord la semaine du {date}",
          },
          {
            date: parseWeekDate(openWeekStart).toLocaleDateString(locale, {
              day: "2-digit",
              month: "short",
            }),
          }
        )
      : undefined;

  return (
    <div className="faf-week-strip">
      <span className="faf-week-strip-label">
        <FormattedMessage
          id="tools_manager_faf_week_strip_label"
          defaultMessage="Tes semaines"
        />
      </span>

      {(weeks || []).map((week) => {
        const stateKey = STATE_LABELS[week.state]
          ? week.state
          : WEEK_STATES.LOCKED;
        const meta = STATE_LABELS[stateKey];
        const resumable =
          week.state === WEEK_STATES.DRAFT &&
          week.declaration_id != null &&
          !!onResume;
        const isCurrent = week.week_start_date === currentWeekStart;
        const hint = blockedHint(week.state);

        return (
          <span
            key={week.week_start_date}
            className="faf-week-chip-slot"
            title={hint}
          >
            <button
              type="button"
              className={`faf-week-chip faf-week-chip--${stateKey.toLowerCase()}`}
              data-testid={`faf-week-cell-${stateKey.toLowerCase()}`}
              aria-current={isCurrent ? "true" : undefined}
              // Only a draft is actionable from here; the other chips are a
              // read-only picture of the window, exactly as the cells were.
              disabled={!resumable}
              onClick={
                resumable ? () => onResume(week.declaration_id) : undefined
              }
            >
              <span className="faf-week-chip-dot" aria-hidden="true" />
              <span className="faf-week-chip-range">
                {formatWeekRange({ isoDate: week.week_start_date, locale })}
              </span>
              <span className="faf-week-chip-state">
                {week.state === WEEK_STATES.SUBMITTED &&
                week.points_total != null ? (
                  <FormattedMessage
                    id="tools_manager_faf_week_cell_points"
                    defaultMessage="{n} pts"
                    values={{ n: week.points_total }}
                  />
                ) : (
                  <FormattedMessage id={meta.id} defaultMessage={meta.label} />
                )}
              </span>
              {hint && <span className="screen-reader-only">{hint}</span>}
            </button>
          </span>
        );
      })}
    </div>
  );
};

WeekStrip.propTypes = {
  weeks: PropTypes.arrayOf(
    PropTypes.shape({
      week_start_date: PropTypes.string,
      state: PropTypes.string,
      points_total: PropTypes.number,
      declaration_id: PropTypes.number,
    })
  ),
  currentWeekStart: PropTypes.string,
  openWeekStart: PropTypes.string,
  onResume: PropTypes.func,
  lang: PropTypes.string,
};

export default WeekStrip;
